import axios from "axios";
import { useContext } from "react";
import { useFieldsRecordContext } from "contextAPI/FieldsRecordContext";
import { handleFieldsErrors } from "./axiosHandleFieldsErros";
import { showAlert } from "components/molecules/Alert/Alert";
import { HttpStatus, errorType, timeout } from "./enums";
import { isNullOrUndefined } from "./validations";
import { useTranslation } from "react-i18next";
import tranlastionEs from "locales/es/translation.json";
import Context from "contextAPI/UserContext";
import swal from "sweetalert";

const useAxiosInterceptor = () => {
  const { t } = useTranslation();
  const { setValidationField, setValidationFieldDatePicker } = useFieldsRecordContext();
  const { passwordExpirationDays } = useContext(Context);

  /**
   * Function that defines an Axios interceptor to handle response errors based on HTTP status codes.
   * @returns {void}
   */
  const interceptor = () => {
    axios.interceptors.request.use(
      (config) => {
        config.timeout = timeout.ONE_HUNDRED_EIGHTY_SECONDS;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error.code === errorType.ECONNABORTED && error.message.includes("timeout")) {
          showAlert({
            text: `(${error.config.endpointError})  ${t("timeout.error.alert")}`,
            icon: "error",
          }).then(() => {
            window.location.href = "/";
          });
          return Promise.reject(error);
        }

        if (!error.response && !error.status) {
          return Promise.reject(error);
        }

        switch (error.response.status) {
          case HttpStatus.BAD_REQUEST:
            if (error.response.status === HttpStatus.BAD_REQUEST) {
              let message_backend_code = error.response.data.code;
              if (message_backend_code === "30010") {
                const errorData = error.response.data.data;
                const isErrorsInFields = handleFieldsErrors(errorData);

                if (isErrorsInFields.length > 0) {
                  message_backend_code = isErrorsInFields[0];
                }
              }

              if (error.response.data instanceof ArrayBuffer) {
                const decoder = new TextDecoder("utf-8");
                const jsonString = decoder.decode(error.response.data);
                message_backend_code = JSON.parse(jsonString).code;
              }

              if (
                (message_backend_code >= 1000 && message_backend_code < 40001) ||
                (message_backend_code > 40001 && message_backend_code < 50046) ||
                (message_backend_code >= 50046 && message_backend_code <= 500096)
              ) {
                if (isNullOrUndefined(error.response.data.data) === false) {
                  setValidationField({
                    id: Object.keys(error.response.data.data),
                    unique: true,
                  });

                  setValidationFieldDatePicker({
                    id: Object.keys(error.response.data.data),
                    required: true,
                  });
                }

                const showAlertServiceError = () => {
                  swal({
                    title: t("modal.DoneError.header"),
                    text: `${tranlastionEs[message_backend_code]}`,
                    icon: "error",
                    button: t("modal.Done.footerButton"),
                    dangerMode: true,
                  }).then(() => {
                    if (
                      Number(message_backend_code) === 30011 ||
                      Number(message_backend_code) === 30029
                    ) {
                      window.location.reload();
                    } else if (
                      Number(message_backend_code) === 500041 ||
                      Number(message_backend_code) === 500042
                    ) {
                      window.location.href = "/";
                    } else if (Number(message_backend_code) === 50059) {
                      window.location.href = "/taskBox";
                    } else {
                      return null;
                    }
                  });
                };
                showAlertServiceError();
              }
              if (message_backend_code === undefined) {
                let message_backend_code_detail = error.response.data.detail;
                const showAlertServerError1 = () => {
                  swal({
                    title: t("modal.DoneError.header"),
                    text: `${message_backend_code_detail}`,
                    icon: "error",
                    button: t("modal.Done.footerButton"),
                    dangerMode: true,
                  });
                };
                showAlertServerError1();
              }
            }
            break;

          case HttpStatus.UNAUTHORIZED:
            if (error.response.status === HttpStatus.UNAUTHORIZED) {
              swal({
                title: t("modal.DoneError.header"),
                text: t("showNotification401"),
                icon: "error",
                button: t("modal.Done.footerButton"),
                dangerMode: true,
              }).then(function () {
                window.location = "/";
                window.localStorage.clear();
              });
            }
            break;

          case HttpStatus.FORBIDDEN:
            if (error.response.status === HttpStatus.FORBIDDEN) {
              const expiration_days = String(passwordExpirationDays);
              if (expiration_days === "0") {
                swal({
                  title: t("modal.DoneError.header"),
                  text: `${t("expirated.password")}`,
                  icon: "info",
                  button: t("modal.Done.footerButton"),
                }).then(function () {
                  window.location = "/renew-password";
                });
              } else {
                let message_backend_code_detail = error.response.data.detail;
                let message_backend_code = error.response.data.code;
                if (message_backend_code >= 20000 && message_backend_code <= 40007) {
                  setValidationField({
                    id: Object.keys(error.response.data.data),
                    unique: true,
                  });
                  const showAlertServiceError = () => {
                    swal({
                      title: t("modal.DoneError.header"),
                      text: `${tranlastionEs[message_backend_code]}`,
                      icon: "error",
                      button: t("modal.Done.footerButton"),
                      dangerMode: true,
                    }).then(function () {
                      window.location = "/dashboard";
                    });
                  };
                  showAlertServiceError();
                }
                if (
                  message_backend_code === undefined ||
                  message_backend_code_detail === HttpStatus.UNAUTHORIZED_PERMISSIONS
                ) {
                  swal({
                    title: t("modal.DoneError.header"),
                    text: t("showNotification403"),
                    icon: "error",
                    button: t("modal.Done.footerButton"),
                    dangerMode: true,
                  }).then((willClose) => {
                    if (willClose) {
                      window.location = "/dashboard";
                    }
                  });
                }
              }
            }
            break;

          case HttpStatus.INTERNAL_SERVER_ERROR:
            if (error.response.status === HttpStatus.INTERNAL_SERVER_ERROR) {
              let message_backend_code_detail = error.response.data.detail;
              const showAlertServerError1 = () => {
                swal({
                  title: t("modal.DoneError.header"),
                  text: `${message_backend_code_detail}`,
                  icon: "error",
                  button: t("modal.Done.footerButton"),
                  dangerMode: true,
                });
              };
              showAlertServerError1();
            }
            {
              let message_backend_code_detail = error.response.data.detail;
              if (message_backend_code_detail === undefined) {
                const showAlertServerError = () => {
                  swal({
                    title: t("modal.DoneError.header"),
                    text: t("showNotification500"),
                    icon: "error",
                    button: t("modal.Done.footerButton"),
                    dangerMode: true,
                  });
                };
                showAlertServerError();
              }
            }
            break;

          default:
            break;
        }
        return Promise.reject(error);
      }
    );
  };

  interceptor();
};

export default useAxiosInterceptor;
